import { FeatureFlag } from '@ps/web-core-enums';
import { generatePath } from '@ps/web-core-utils';
import type { HgClubInfo } from '@ps/web-shared-api-types/v0/SwHomeGames';
import { Link, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import { useHomeGamesWidget } from '@swf/poker';

import CreateClubButton from '../../MyClubs/CreateClubButton/SidebarCreateClubButton';
import JoinClubButton from '../../MyClubs/JoinClubButton/SidebarJoinClubButton';

import ClubItem from './SidebarNavClubItem';
import {
  TXTCLI_Manager,
  TXTCLI_Member,
  TXTWCL_All_Clubs,
  TXTWCL_Explore_2
} from './SplitClubsSidebarNav.messages.json';
import styles from './SplitClubsSidebarNav.scss';

interface ListItem {
  club: HgClubInfo;
  index: number;
}

const ClubListItem = ({ club, index }: ListItem) => {
  const { pathname } = useLocation();

  return (
    <li
      className={classNames(styles.subNavItem, {
        [styles.active]: pathname.includes(`${club.clubId}`)
      })}
    >
      <ClubItem club={club} index={index} />
    </li>
  );
};

const mapClubs = (club: HgClubInfo, index: number) => (
  <ClubListItem key={club.clubId} club={club} index={index} />
);

const Separator = () => <div className={styles.separator} />;

const SplitClubsSidebarNav = () => {
  const { widgetSelect } = useHomeGamesWidget();
  const isPublicClubsEnabled = useFeature(FeatureFlag.PKRHomeGamesPublicClubSearch);
  const aliases = useSelector(select.cms.getAliases);
  const joinedClubs = useSelector(widgetSelect.joinedClubs);
  const ownedClubs = useSelector(widgetSelect.ownedClubs);
  const { pathname } = useLocation();

  return (
    <div className={styles.container}>
      <ul className={styles.nav}>
        <li
          className={classNames(styles.navItem, {
            [styles.active]: pathname === generatePath(aliases?.homeGamesClubs ?? '', {})
          })}
        >
          <Link alias="homeGamesClubs">
            <Translation {...TXTWCL_All_Clubs} />
          </Link>
        </li>
        {isPublicClubsEnabled && (
          <li
            className={classNames(styles.navItem, {
              [styles.active]: pathname === generatePath(aliases.homeGamesPublicClubs ?? '', {})
            })}
          >
            <Link alias="homeGamesPublicClubs">
              <Translation {...TXTWCL_Explore_2} />
            </Link>
          </li>
        )}
        <CreateClubButton />
        <JoinClubButton />
        <Separator />
        <div className={styles.clubs}>
          <span className={styles.clubsTitle}>
            <Translation {...TXTCLI_Manager} />
          </span>
          {!isEmpty(ownedClubs) && <>{ownedClubs.map(mapClubs)}</>}
        </div>
        <Separator />
        <div className={styles.clubs}>
          <span className={styles.clubsTitle}>
            <Translation {...TXTCLI_Member} />
          </span>
          {!isEmpty(joinedClubs) && <>{joinedClubs.map(mapClubs)}</>}
        </div>
      </ul>
    </div>
  );
};

export default SplitClubsSidebarNav;
